import { FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { detectDocument, exportToWord } from '@/services/exportService';
import { saveDocument, detectDocumentType } from '@/services/documentHistoryService';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { checkFuncionarioQuota, invalidateFuncionarioQuotaCache } from '@/services/funcionarioQuotaService';
import { useNavigate } from 'react-router-dom';

interface DocumentDownloadProps {
  content: string;
  chatId?: string;
  messageId?: string;
}

export function DocumentDownload({ content, chatId, messageId }: DocumentDownloadProps) {
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const docInfo = detectDocument(content);

  if (!docInfo.isDocument) {
    return null;
  }

  const handleDownload = async () => {
    // Comprobar cuota diaria de funcionarios free antes de descargar
    if (user && profile) {
      try {
        const quota = await checkFuncionarioQuota(user.id, profile.plan, profile, 'documents');
        if (!quota.allowed) {
          toast({
            title: 'Has alcanzado tu cuota diaria',
            description: `Plan gratuito: ${quota.limit} documento${quota.limit === 1 ? '' : 's'} al día. Mejora tu plan para descargar sin límites.`,
            variant: 'destructive',
          });
          navigate('/pricing');
          return;
        }
      } catch (e) {
        console.warn('[DocumentDownload] quota check failed:', e);
      }
    }
    try {
      await exportToWord(content, docInfo.filename);
      
      // Save to document history
      if (user) {
        try {
          await saveDocument(user.id, {
            title: docInfo.title,
            content: content,
            document_type: detectDocumentType(content),
            chat_id: chatId,
            message_id: messageId,
          });
        } catch (saveError) {
          console.error('Error saving to history:', saveError);
        }
        // Register in downloads
        try {
          const { saveDownload } = await import('@/services/downloadHistoryService');
          saveDownload({
            userId: user.id,
            title: docInfo.title,
            downloadType: 'document',
            mode: 'funcionarios',
            metadata: { content },
          });
        } catch (e) {
          console.error('Error saving download record:', e);
        }
        invalidateFuncionarioQuotaCache();
      }

      toast({
        title: "Documento descargado",
        description: `${docInfo.title} se ha guardado como Word.`,
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Error al descargar",
        description: "No se pudo generar el documento Word.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-3 p-3 bg-primary/5 border border-primary/10 rounded-lg flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#2B579A] flex items-center justify-center">
          <FileText className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{docInfo.title}</p>
          <p className="text-xs text-muted-foreground">Documento Word (.docx)</p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className="gap-2"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">Descargar</span>
      </Button>
    </div>
  );
}
